import React, { useMemo } from 'react';
import classNames from 'classnames';

import {
  buildRecurringRideSummary,
  isRecurringRideSummaryReady,
} from '../../../utils/buildRecurringRideSummary';
import { DATE_TIME_FIELD_LABELS } from '../../forms/CreateRide/StepThree/stepThreeDateTimeConstants';

import styles from './index.module.css';

/**
 * Recurring series summary card (Step 3, Step 5 review, Quick Schedule).
 * @param {'form'|'review'} variant form = Step 3 spacing; review = Step 5 spacing
 * @param {boolean} dense smaller label/value fonts (Quick Schedule only)
 */
export const RecurringRidePlanSummary = ({
  isRecurring,
  isRoundtrip,
  recurrenceRule,
  pickUpDate,
  variant = 'form',
  dense = false,
  className,
}) => {
  const show = useMemo(
    () => Boolean(isRecurring) && isRecurringRideSummaryReady(recurrenceRule),
    [
      isRecurring,
      recurrenceRule?.frequency,
      recurrenceRule?.count,
      recurrenceRule?.interval,
      recurrenceRule?.isCustom,
      recurrenceRule?.monthlyType,
      recurrenceRule?.byDays,
    ],
  );

  const summary = useMemo(() => {
    if (!show) return null;
    return buildRecurringRideSummary(recurrenceRule, pickUpDate, Boolean(isRoundtrip));
  }, [
    show,
    isRoundtrip,
    pickUpDate?.datetime,
    pickUpDate?.timezone,
    recurrenceRule?.startPoint?.datetime,
    recurrenceRule?.startPoint?.timezone,
    recurrenceRule?.frequency,
    recurrenceRule?.count,
    recurrenceRule?.interval,
    recurrenceRule?.isCustom,
    recurrenceRule?.monthlyType,
    recurrenceRule?.weekIndex,
    recurrenceRule?.byDays,
  ]);

  if (!show) return null;

  return (
    <div
      className={classNames(styles.root, className, {
        [styles.rootReview]: variant === 'review',
        [styles.rootForm]: variant === 'form',
        [styles.rootDense]: dense,
      })}
    >
      <h3 className={styles.heading}>{DATE_TIME_FIELD_LABELS.RIDE_SUMMARY_TITLE}</h3>
      <div className={styles.planCard}>
        <div className={styles.planTopRow}>
          <div className={styles.planCell}>
            <span className={styles.planLabel}>{DATE_TIME_FIELD_LABELS.RIDE_SUMMARY_FIRST_RIDE}</span>
            <span className={styles.planValue}>{summary?.startDate || '—'}</span>
          </div>
          <div className={styles.planCell}>
            <span className={styles.planLabel}>{DATE_TIME_FIELD_LABELS.RIDE_SUMMARY_LAST_RIDE}</span>
            <span className={styles.planValue}>{summary?.endDate || '—'}</span>
          </div>
          <div className={classNames(styles.planCell, styles.planCellTotal)}>
            <span className={styles.planLabel}>{DATE_TIME_FIELD_LABELS.RIDE_SUMMARY_TOTAL_RIDES}</span>
            <span className={styles.planValue}>{summary?.numberOfRides || '—'}</span>
          </div>
        </div>
        <div className={styles.planBottomRow}>
          <span className={styles.planLabel}>{DATE_TIME_FIELD_LABELS.RIDE_SUMMARY_REPEATS}</span>
          <span className={styles.planValue}>{summary?.repeats || '—'}</span>
        </div>
      </div>
    </div>
  );
};
